import React from 'react';
import cx from 'classnames';
import SingleSampleAccessPoint, {
  APSamplePreviewModule,
} from 'cards/components/common/Samples/SingleSampleAccessPoint';
import type { APSamplePreviewModuleProps } from 'cards/components/common/Samples/SingleSampleAccessPoint/SingleSampleAccessPointUI/APSamplePreviewModule/APSamplePreviewModule';
import { CARD_TYPES } from 'cards/constants/CardTypes';
import { getUserWeddingDate } from 'selectors/user/userSelectors';
import { getThemeHexColor } from 'selectors/wedding';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import pluralize from '@zola-helpers/client/dist/es/transformers/pluralize';
import styles from './PaperPreviewCrossSellModule.module.less';

// trackingParams.section propogated to single sample tracking as 'entryPoint' prop
interface PaperPreviewCrossSellModuleProps
  extends Pick<
    APSamplePreviewModuleProps,
    | 'trackingParams'
    | 'backgroundColor'
    | 'photoUrl'
    | 'samplePreviewPropOverrides'
    | 'productColorPickerPropOverrides'
    | 'showSwatchArrow'
    | 'disableResponsiveStyling'
    | 'samplePreviewCustomClassName'
  > {
  themeKey?: string;
  hedPrePend?: string;
  pluralHed?: boolean;
  cardType?: string;
  ctaType?: 'button' | 'link';
  ctaTextOverride?: string;
  className?: string;
  showIsMatchingTheme?: boolean;
}

const PaperPreviewCrossSellModule: React.FC<PaperPreviewCrossSellModuleProps> = ({
  themeKey,
  photoUrl,
  trackingParams,
  backgroundColor,
  hedPrePend,
  pluralHed,
  samplePreviewPropOverrides,
  productColorPickerPropOverrides,
  showSwatchArrow,
  disableResponsiveStyling,
  cardType: cardTypeProp,
  samplePreviewCustomClassName,
  ctaType = 'button',
  ctaTextOverride,
  className,
  showIsMatchingTheme,
}) => {
  const weddingDate = useSelector(getUserWeddingDate);
  const monthsToWedding = dayjs(weddingDate).diff(new Date(), 'month');
  const showInvites = monthsToWedding < 6;
  const conditionalCardType = showInvites ? CARD_TYPES.invitation : CARD_TYPES.std;
  const cardType = cardTypeProp || conditionalCardType;

  const formatHeader = () => {
    return pluralize(
      `${hedPrePend || 'Get'} a FREE ${
        cardType === CARD_TYPES.invitation ? 'Invites' : 'Save the Date'
      } Sample`,
      `Get free ${cardType === CARD_TYPES.invitation ? 'invitation' : 'save the date'} samples`,
      pluralHed ? 2 : 1,
      false
    );
  };

  // Theme fetched for all /wedding/manage pages
  const selectedWebsiteColor = useSelector(getThemeHexColor);

  return (
    <SingleSampleAccessPoint
      familyName={themeKey}
      sampleCardType={cardType}
      cardSuiteFilterFn={suites => {
        // Temp workaround for selecting next most popular STD template under violin family
        // b/c first template allows has 3 custom image elements and single sample flow only allows for customization of one
        if (suites.length > 1 && themeKey === 'violin' && cardType === CARD_TYPES.std) {
          return suites[1];
        }
        return suites[0];
      }}
      displayComponent={
        <APSamplePreviewModule
          hed={formatHeader()}
          ctaType={ctaType}
          ctaTextOverride={ctaTextOverride}
          trackingParams={trackingParams}
          backgroundColor={backgroundColor}
          photoUrl={photoUrl}
          initialColor={selectedWebsiteColor}
          samplePreviewPropOverrides={samplePreviewPropOverrides}
          productColorPickerPropOverrides={productColorPickerPropOverrides}
          className={cx(styles.apSamplePreviewOverride, className)}
          disableResponsiveStyling={disableResponsiveStyling}
          showSwatchArrow={showSwatchArrow}
          samplePreviewCustomClassName={samplePreviewCustomClassName}
          showIsMatchingTheme={showIsMatchingTheme}
          // previewPhotoSelectionOverride={[WPreviewPhotoView.SourceEnum.WEBSITE]}
        />
      }
    />
  );
};

export default PaperPreviewCrossSellModule;
